// Mock data store — realistic fictional data for the Goat Bar Management System.
// Designed to be replaced by Lovable Cloud queries in the future.

export type Unidade = "Eventos" | "7Steakhouse" | "Goat Botequim";

export interface ModalityConfig {
  active: boolean;
  cost: number;
  price?: number;
}

export interface Insumo {
  nome: string;
  custo: number;
}

export interface Drink {
  id: string;
  nome: string;
  categoria: string;
  descricao: string;
  custoUnitario: number; // Exclusivo para Eventos
  insumos?: Insumo[];
  modalityConfig: {
    evento: ModalityConfig;
    steakhouse: ModalityConfig;
    goatbotequim: ModalityConfig;
  };
  imagem?: string;
  showInPublicMenu?: boolean;
}

export interface Venda {
  id: string;
  data: string; // ISO
  unidade: Unidade;
  drinkId: string;
  drinkNome: string;
  quantidade: number;
  precoUnitario: number;
  custoUnitario: number;
  observacoes?: string;
}

export interface SalesSessionItem {
  drinkId: string;
  nome: string;
  quantidade: number;
  precoUnitario: number;
  custoUnitario: number;
  custoInsumo?: number;
}

export interface FinancialSession {
  id: string;
  data: string; // ISO
  modalidade: "Goat Botequim" | "7Steakhouse";
  items: SalesSessionItem[];
  maoDeObraValor: number;
  maoDeObraQtd: number;
  maoDeObraNomes?: string;
  maoDeObraDetalhes?: { data: string; valor: number; qtdPessoas: number; nomes?: string }[];
  reposicaoRestaurante?: number; // Steakhouse only (soma total)
  custosRestauranteDetalhes?: { descricao: string; valor: number }[];
  observacoes?: string;
}

export interface InventoryItem {
  id: string;
  nome: string;
  quantidadeTotal: number;
  observacoes: string;
}

export interface GastoDiverso {
  id: string;
  descricao: string;
  valor: number;
}

export interface EventoHistoricoAlteracao {
  data: string; // ISO
  usuario?: string;
  camposAlterados: string[];
  valorAntigo: string;
  valorNovo: string;
}

export interface EventoHistoricoNegociacao {
  data: string;
  status: string;
  observacao: string;
}

export type EventoStatus =
  | "DADOS_SOLICITADOS"
  | "ORCAMENTO_ENVIADO"
  | "AGUARDANDO_RESPOSTA"
  | "CONFIRMADO"
  | "FINALIZADO"
  | "CANCELADO"
  | "NOVO"
  | "em_assinatura"
  | "dados_solicitados";

export interface Evento {
  id: string;
  nome: string;
  evento_nome?: string;
  cliente: string;
  nomeNoivo?: string;
  nomeNoiva?: string;
  telefone: string;
  email: string;
  data: string;
  horario: string;
  duracao?: number | "";
  local: string;
  cidade: string;
  tipo: string;
  convidados: number;
  drinks: string[]; // drink ids
  drinkCustomizations: Record<string, import("./drink-customization").DrinkCustomization>;
  observacoes: string;
  status: EventoStatus;
  contratoId?: string;

  // Novos campos de orçamento (Versão Atual)
  drinksPorPessoa: number;
  markupAdicionalDrinks: number;
  hasWelcomeDrinks: boolean;
  welcomeDrinksPerPerson: number;
  welcomeDrinksProfitPercentage: number;
  welcomeDrinksSelected: import("./additional-budget-items").WelcomeDrinkSelection[];
  hasShots: boolean;
  shotsItems: import("./additional-budget-items").ShotBudgetItem[];
  equipe: {
    bartender: { qtd: number; valorUnitario: number };
    keeper: { qtd: number; valorUnitario: number };
    copeira: { qtd: number; valorUnitario: number };
  };
  gelo: { pacotesEstimados?: number; pacotesOverride?: number; valorUnitario: number };
  viagem: { incluir: boolean; valor: number };
  gastosDiversos: GastoDiverso[];
  lucroDesejado: number;
  pagamento: {
    formaPagamento: string;
    percentualPago: number;
    dataPagamento?: string;
  };
  coposVinculados: Record<string, string>; // drinkId -> glasswareId
  drinksSnapshots?: Array<{
    drinkId: string;
    nome: string;
    custoUnitario: number;
    copoId?: string;
    copoNome?: string;
  }>;
  historicoAlteracoes: EventoHistoricoAlteracao[];
  historicoNegociacao: EventoHistoricoNegociacao[];
  lead_source?: string;
  referral_name?: string;
  is_paid_full?: boolean;

  // Legacy/Computed
  valorNegociado: number;
  custoPrevisto: number;
  desconto?: number;
  descontoMotivo?: string;
  descontos?: { valor: number; motivo: string; deduzirCustoDrinks?: boolean }[];
  descricaoBebidas?: string;
  bebidas: string[];
  /** Raw line-based editor value; spaces are normalized only when the budget is saved. */
  bebidasInput: string;
}

export interface Contrato {
  id: string;
  eventoId: string;
  cliente: string;
  status: "rascunho" | "enviado" | "assinado";
  criadoEm: string;
  template: string;
}

export interface ContractTemplate {
  id: string;
  name: string;
  fileUrl: string;
  fileType: "pdf" | "docx";
  isDefault: boolean;
  variablesSchema: string[];
  createdAt: string;
}

export interface ContractSigner {
  id: string;
  name: string;
  cpf: string;
  email: string;
  phone: string;
  role: string;
  address: string;
  isActive: boolean;
}

export interface Glassware {
  id: string;
  name: string;
  type: string;
  replacementValue: number;
  isActive: boolean;
}

export interface EventContractClientData {
  id: string;
  eventId: string;
  clientName: string;
  cpfCnpj: string;
  phone: string;
  email: string;
  address: string;
  legalRepresentativeName: string;
  legalRepresentativeCpf: string;
  notes: string;
  submittedAt: string;
}

export type EventContractStatus =
  | "aguardando_dados"
  | "dados_recebidos"
  | "gerado"
  | "enviado_assinatura"
  | "assinado_parcialmente"
  | "assinado"
  | "arquivado"
  | "cancelado";

export interface EventContract {
  id: string;
  eventId: string;
  templateId: string;
  signerId: string;
  status: EventContractStatus;
  generatedFileUrl?: string;
  signedFileUrl?: string;
  signatureCertificateUrl?: string;
  version: number;
  generatedAt?: string;
  sentForSignatureAt?: string;
  fullySignedAt?: string;
}

export interface ContractSignatureHistory {
  id: string;
  eventContractId: string;
  signerName: string;
  signerEmail: string;
  signerRole: string; // 'contratante' ou 'contratada'
  status: "pending" | "signed" | "error";
  signedAt?: string;
  ipAddress?: string;
}

export interface ContractHistory {
  id: string;
  eventContractId: string;
  action: string;
  notes?: string;
  createdAt: string;
}

export interface ParametroCalculo {
  id: string;
  chave: string;
  label: string;
  valor: number;
  unidade: string;
  groupo: "Repasse" | "Custos" | "Consumo" | "Operacional";
  descricao?: string;
}

export interface TipoEvento {
  id: string;
  nome: string;
  consumoBebidaPessoa: number; // doses por convidado
  geloKgPessoa: number;
  insumosPessoa: number; // R$
  equipePor50: number; // pessoas de equipe a cada 50 convidados
}

// ─── Drinks ───────────────────────────────────────────────────────────────

export const drinks: Drink[] = [
  {
    id: "caipirinha",
    nome: "Caipirinha",
    categoria: "Cachaça",
    descricao: "Cachaça, limão e açúcar.",
    custoUnitario: 2.37,
    insumos: [],
    modalityConfig: {
      evento: { active: false, cost: 0 },
      steakhouse: { active: true, cost: 13.25, price: 25 },
      goatbotequim: { active: true, cost: 2.37, price: 20 },
    },
    imagem: "/drinks/caipi-limao.png",
  },
  {
    id: "caipivodka-limao",
    nome: "Caipivodka Limão",
    categoria: "Vodka",
    descricao: "Vodka, limão e açúcar.",
    custoUnitario: 2.2,
    insumos: [
      { nome: "Vodka", custo: 2.0 },
      { nome: "Limão", custo: 0.2 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 2.2 },
      steakhouse: { active: false, cost: 0 },
      goatbotequim: { active: true, cost: 3.87, price: 22 },
    },
    imagem: "/drinks/caipi-limao.png",
  },
  {
    id: "caipi-limao-cravo-mel",
    nome: "Caipivodka limão cravo e mel",
    categoria: "Vodka",
    descricao: "Vodka, limão cravo e mel.",
    custoUnitario: 2.7,
    insumos: [
      { nome: "Vodka", custo: 2.0 },
      { nome: "Limão", custo: 0.2 },
      { nome: "Cravo", custo: 0.1 },
      { nome: "Mel", custo: 0.4 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 2.7 },
      steakhouse: { active: true, cost: 13.25, price: 30 },
      goatbotequim: { active: true, cost: 4.37, price: 22 },
    },
    imagem: "/drinks/caipi-limao-cravo-mel.png",
  },
  {
    id: "caipivodka-morango",
    nome: "Caipivodka Morango",
    categoria: "Vodka",
    descricao: "Vodka e morangos frescos.",
    custoUnitario: 3.6,
    insumos: [
      { nome: "Vodka", custo: 2.0 },
      { nome: "Morango", custo: 1.6 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 3.6 },
      steakhouse: { active: true, cost: 15.4, price: 30 },
      goatbotequim: { active: true, cost: 5.37, price: 22 },
    },
    imagem: "/drinks/caipi-morango.png",
  },
  {
    id: "caipivodka-abacaxi",
    nome: "Caipivodka Abacaxi",
    categoria: "Vodka",
    descricao: "Vodka, abacaxi e raspas de limão.",
    custoUnitario: 2.8,
    insumos: [
      { nome: "Vodka", custo: 2.0 },
      { nome: "Abacaxi", custo: 0.5 },
      { nome: "Raspas", custo: 0.3 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 2.8 },
      steakhouse: { active: true, cost: 15.4, price: 30 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/caipi-abacaxi.png",
  },
  {
    id: "caipivodka-maracuja",
    nome: "Caip Maracujá com baunilha",
    categoria: "Vodka",
    descricao: "Vodka, maracujá e açúcar de baunilha.",
    custoUnitario: 3.2,
    insumos: [
      { nome: "Vodka", custo: 2.0 },
      { nome: "Maracujá", custo: 0.9 },
      { nome: "Açúcar Baunilha", custo: 0.3 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 3.2 },
      steakhouse: { active: true, cost: 15.4, price: 30 },
      goatbotequim: { active: true, cost: 4.47, price: 22 },
    },
    imagem: "/drinks/caipi-maracuja-baunilha.png",
  },
  {
    id: "moscow-mule",
    nome: "Moscow Mule",
    categoria: "Vodka",
    descricao: "Vodka, limão e sifão de gengibre.",
    custoUnitario: 3.1,
    insumos: [
      { nome: "Vodka", custo: 2.0 },
      { nome: "Limão", custo: 0.2 },
      { nome: "Sifão", custo: 0.9 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 3.1 },
      steakhouse: { active: true, cost: 17.55, price: 35 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/moscow-mule.png",
  },
  {
    id: "london-mule",
    nome: "London Mule",
    categoria: "Gin",
    descricao: "Gin, limão e sifão de gengibre.",
    custoUnitario: 5.76,
    insumos: [
      { nome: "Gin", custo: 4.66 },
      { nome: "Limão", custo: 0.2 },
      { nome: "Sifão", custo: 0.9 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 5.76 },
      steakhouse: { active: true, cost: 18.55, price: 35 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/moscow-mule.png",
  },
  {
    id: "mojito",
    nome: "Mojito",
    categoria: "Rum",
    descricao: "Vodka, limão, hortelã e água com gás.",
    custoUnitario: 3.0,
    insumos: [
      { nome: "Vodka", custo: 2.0 },
      { nome: "Limão", custo: 0.2 },
      { nome: "Hortelã", custo: 0.5 },
      { nome: "Água com gás", custo: 0.3 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 3.0 },
      steakhouse: { active: true, cost: 16.76, price: 32 },
      goatbotequim: { active: true, cost: 4.67, price: 25 },
    },
    imagem: "/drinks/mojito.png",
  },
  {
    id: "aquario",
    nome: "Aquário",
    categoria: "Vodka",
    descricao: "Vodka, limão, curaçao, alecrim e água com gás.",
    custoUnitario: 4.25,
    insumos: [
      { nome: "Vodka", custo: 2.0 },
      { nome: "Limão", custo: 0.2 },
      { nome: "Curaçao", custo: 1.0 },
      { nome: "Alecrim", custo: 0.25 },
      { nome: "Açúcar", custo: 0.5 },
      { nome: "Água com gás", custo: 0.3 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 4.25 },
      steakhouse: { active: true, cost: 17.26, price: 32 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/aquario.jpg",
  },
  {
    id: "sex-on-the-beach",
    nome: "Sex on The Beach",
    categoria: "Vodka",
    descricao: "Vodka, suco de laranja, xarope de pêssego e grenadine.",
    custoUnitario: 6.62,
    insumos: [
      { nome: "Vodka", custo: 2.0 },
      { nome: "Suco laranja", custo: 0.8 },
      { nome: "Xarope pêssego", custo: 2.64 },
      { nome: "Grenadine", custo: 0.88 },
      { nome: "Jujuba", custo: 0.3 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 6.62 },
      steakhouse: { active: true, cost: 17.76, price: 32 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/sex-on-the-beach.png",
  },
  {
    id: "bossa-nova",
    nome: "Bossa Nova",
    categoria: "Vodka",
    descricao: "Vodka, uva e água de coco.",
    custoUnitario: 4.0,
    insumos: [
      { nome: "Vodka", custo: 2.0 },
      { nome: "Uva", custo: 1.0 },
      { nome: "Água de coco", custo: 1.0 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 4.0 },
      steakhouse: { active: false, cost: 0 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/bossa-nova.png",
  },
  {
    id: "gin-tonica",
    nome: "Gin Tônica",
    categoria: "Gin",
    descricao: "Gin, tônica, limão siciliano e especiarias.",
    custoUnitario: 6.71,
    insumos: [
      { nome: "Gin", custo: 4.66 },
      { nome: "Tônica", custo: 1.5 },
      { nome: "Limão siciliano", custo: 0.3 },
      { nome: "Especiaria", custo: 0.25 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 6.71 },
      steakhouse: { active: true, cost: 18.55, price: 35 },
      goatbotequim: { active: true, cost: 8.17, price: 28 },
    },
    imagem: "/drinks/gin-tonica.png",
  },
  {
    id: "gin-tropical",
    nome: "Gin Tropical",
    categoria: "Gin",
    descricao: "Gin, energético tropical, laranja e especiarias.",
    custoUnitario: 6.5,
    insumos: [
      { nome: "Gin", custo: 4.66 },
      { nome: "Energético Tropical", custo: 1.5 },
      { nome: "Laranja", custo: 0.34 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 6.5 },
      steakhouse: { active: true, cost: 18.55, price: 35 },
      goatbotequim: { active: true, cost: 8.5, price: 28 },
    },
    imagem: "/drinks/gin-tropical.png",
  },
  {
    id: "gin-morango",
    nome: "Gin Morango",
    categoria: "Gin",
    descricao: "Gin, morangos frescos, tônica e especiarias.",
    custoUnitario: 6.8,
    insumos: [
      { nome: "Gin", custo: 4.66 },
      { nome: "Morango", custo: 1.6 },
      { nome: "Tônica", custo: 0.54 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 6.8 },
      steakhouse: { active: true, cost: 18.55, price: 35 },
      goatbotequim: { active: true, cost: 8.5, price: 28 },
    },
    imagem: "/drinks/gin-morango.png",
  },
  {
    id: "tom-collins",
    nome: "Tom Collins",
    categoria: "Gin",
    descricao: "Gin, limão, água com gás e cereja.",
    custoUnitario: 5.91,
    insumos: [
      { nome: "Gin", custo: 4.66 },
      { nome: "Limão", custo: 0.2 },
      { nome: "Água com gás", custo: 0.3 },
      { nome: "Cereja", custo: 0.75 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 5.91 },
      steakhouse: { active: true, cost: 19.05, price: 35 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/tom-collins.png",
  },
  {
    id: "fitzgerald",
    nome: "Fitzgerald",
    categoria: "Gin",
    descricao: "Gin, limão, angostura e twist.",
    custoUnitario: 7.23,
    insumos: [
      { nome: "Gin", custo: 4.0 },
      { nome: "Limão", custo: 0.2 },
      { nome: "Angostura", custo: 2.83 },
      { nome: "Twist", custo: 0.2 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 7.23 },
      steakhouse: { active: true, cost: 19.55, price: 35 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/fitzgerald.png",
  },
  {
    id: "bramble",
    nome: "Bramble",
    categoria: "Gin",
    descricao: "Gin, limão e xarope de amora.",
    custoUnitario: 7.06,
    insumos: [
      { nome: "Gin", custo: 4.66 },
      { nome: "Limão", custo: 0.2 },
      { nome: "Xarope amora", custo: 1.4 },
      { nome: "Guarnição", custo: 0.8 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 7.06 },
      steakhouse: { active: false, cost: 0 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/bramble.jpeg",
  },
  {
    id: "aperol-spritz",
    nome: "Aperol Spritz",
    categoria: "Aperitivos",
    descricao: "Aperol, champanhe, laranja e água com gás.",
    custoUnitario: 10.5,
    insumos: [
      { nome: "Aperol", custo: 4.0 },
      { nome: "Champanhe", custo: 6.0 },
      { nome: "Laranja", custo: 0.2 },
      { nome: "Água com gás", custo: 0.3 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 10.5 },
      steakhouse: { active: true, cost: 20.5, price: 35 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/aperol-spritz.png",
  },
  {
    id: "cest-la-vie",
    nome: "C'est Lá Vie",
    categoria: "Gin",
    descricao: "Xarope, limão, champanhe, água com gás e gelo.",
    custoUnitario: 9.51,
    insumos: [
      { nome: "Xarope", custo: 2.64 },
      { nome: "Limão", custo: 0.2 },
      { nome: "Champanhe", custo: 6.0 },
      { nome: "Água com gás", custo: 0.3 },
      { nome: "Gelo", custo: 0.37 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 9.51 },
      steakhouse: { active: true, cost: 22.7, price: 40 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/cest-la-vie.png",
  },
  {
    id: "mint-jullep",
    nome: "Mint Jullep",
    categoria: "Whisky",
    descricao: "Whisky, limão e hortelã.",
    custoUnitario: 5.5,
    insumos: [
      { nome: "Whisky", custo: 5.0 },
      { nome: "Limão", custo: 0.2 },
      { nome: "Hortelã", custo: 0.3 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 5.5 },
      steakhouse: { active: true, cost: 21.2, price: 40 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/mint-julep.png",
  },
  {
    id: "whisky-sour",
    nome: "Whisky Sour",
    categoria: "Whisky",
    descricao: "Whisky, limão, proteína e guarnição.",
    custoUnitario: 5.8,
    insumos: [
      { nome: "Whisky", custo: 5.0 },
      { nome: "Limão", custo: 0.2 },
      { nome: "Proteína", custo: 0.3 },
      { nome: "Guarnição", custo: 0.3 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 5.8 },
      steakhouse: { active: true, cost: 21.7, price: 40 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/whisky-sour.png",
  },
  {
    id: "negroni",
    nome: "Negroni",
    categoria: "Gin",
    descricao: "Vermute, gin e campari.",
    custoUnitario: 9.7,
    insumos: [
      { nome: "Vermute", custo: 2.2 },
      { nome: "Gin", custo: 4.0 },
      { nome: "Campari", custo: 3.5 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 9.7 },
      steakhouse: { active: true, cost: 25.35, price: 45 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/negroni.png",
  },
  {
    id: "campari-tonica",
    nome: "Campari Tônica",
    categoria: "Aperitivos",
    descricao: "Campari, tônica e twist laranja.",
    custoUnitario: 9.7,
    insumos: [
      { nome: "Tônica", custo: 1.5 },
      { nome: "Campari", custo: 8.0 },
      { nome: "Twist laranja", custo: 0.2 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 9.7 },
      steakhouse: { active: false, cost: 0 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=400&auto=format&fit=crop",
  },
  {
    id: "raspberry",
    nome: "Raspberry",
    categoria: "Vodka",
    descricao: "Vodka, coulis, espuma.",
    custoUnitario: 4.0,
    insumos: [
      { nome: "Vodka", custo: 4.0 },
      { nome: "Coulis", custo: 0.0 },
      { nome: "Espuma", custo: 0.0 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 4.0 },
      steakhouse: { active: false, cost: 0 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "https://images.unsplash.com/photo-1556679343-c7306c1976bc?w=400&auto=format&fit=crop",
  },
  {
    id: "stamping",
    nome: "Stamping Passion",
    categoria: "Vodka",
    descricao: "Vodka, maracujá, limão, açúcar baunilha e tabasco.",
    custoUnitario: 3.9,
    insumos: [
      { nome: "Vodka", custo: 2.0 },
      { nome: "Maracujá", custo: 0.9 },
      { nome: "Limão", custo: 0.2 },
      { nome: "Açúcar baunilha", custo: 0.3 },
      { nome: "Tabasco", custo: 0.4 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 3.9 },
      steakhouse: { active: true, cost: 20.7, price: 40 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/stamping-passion.jpg",
  },
  {
    id: "olho-grego",
    nome: "Olho Grego",
    categoria: "Vodka",
    descricao: "Vodka, limão, xarope amêndoas, xarope curaçao.",
    custoUnitario: 7.0,
    insumos: [
      { nome: "Vodka", custo: 4.0 },
      { nome: "Limão", custo: 0.2 },
      { nome: "Xarope amêndoas", custo: 1.4 },
      { nome: "Xarope curaçao", custo: 1.4 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 7.0 },
      steakhouse: { active: false, cost: 0 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/olho-grego.jpeg",
  },
  {
    id: "cosmopolitan",
    nome: "Cosmopolitan",
    categoria: "Vodka",
    descricao: "Vodka, xarope cramberry, limão e cointreau.",
    custoUnitario: 8.97,
    insumos: [
      { nome: "Vodka", custo: 4.0 },
      { nome: "Xarope Cramberry", custo: 2.1 },
      { nome: "Limão", custo: 0.2 },
      { nome: "Cointreau", custo: 2.57 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 8.97 },
      steakhouse: { active: false, cost: 0 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/cosmopolitan.jpeg",
  },
  {
    id: "apple-martini",
    nome: "Apple Martini",
    categoria: "Vodka",
    descricao: "Xarope e vodka.",
    custoUnitario: 3.7,
    insumos: [
      { nome: "Xarope", custo: 2.2 },
      { nome: "Vodka", custo: 2.0 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 3.7 },
      steakhouse: { active: true, cost: 20.7, price: 40 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/apple-martini.png",
  },
  {
    id: "expresso-martini",
    nome: "Expresso Martini",
    categoria: "Vodka",
    descricao: "Vodka, açúcar baunilha, bailays e café.",
    custoUnitario: 4.86,
    insumos: [
      { nome: "Vodka", custo: 2.0 },
      { nome: "Açúcar baunilha", custo: 0.5 },
      { nome: "Bailays", custo: 1.86 },
      { nome: "Café", custo: 0.5 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 4.86 },
      steakhouse: { active: true, cost: 20.7, price: 40 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/expresso-martini.png",
  },
  {
    id: "paloma",
    nome: "Paloma",
    categoria: "Tequila",
    descricao: "Tequila, xarope grapefruit, limão e grapefruit.",
    custoUnitario: 13.98,
    insumos: [
      { nome: "Tequila", custo: 10.73 },
      { nome: "Xarope grapefruit", custo: 2.8 },
      { nome: "Limão", custo: 0.2 },
      { nome: "Grapefruit", custo: 0.25 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 13.98 },
      steakhouse: { active: false, cost: 0 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/paloma.jpeg",
  },
  {
    id: "soda-italiana",
    nome: "Soda Italiana",
    categoria: "Sem Álcool",
    descricao: "Xarope e água com gás.",
    custoUnitario: 3.34,
    insumos: [
      { nome: "Xarope", custo: 2.64 },
      { nome: "Água com gás", custo: 0.7 },
    ],
    modalityConfig: {
      evento: { active: true, cost: 3.34 },
      steakhouse: { active: true, cost: 3.34, price: 25 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/soda-italiana.png",
  },
  {
    id: "old-fashioned",
    nome: "Old Fashioned",
    categoria: "Whisky",
    descricao: "Whisky premium, angostura e açúcar.",
    custoUnitario: 24.35,
    insumos: [
      { nome: "Whisky premium", custo: 20.0 },
      { nome: "Angostura", custo: 2.85 },
      { nome: "Açúcar", custo: 1.5 },
    ],
    modalityConfig: {
      evento: { active: false, cost: 0 },
      steakhouse: { active: true, cost: 24.35, price: 45 },
      goatbotequim: { active: false, cost: 0 },
    },
    imagem: "/drinks/old-fashioned.png",
  },

  // --- DOSES BOTEQUIM ---
  {
    id: "dose-cachaca",
    nome: "Cachaça (Dose)",
    categoria: "Doses",
    descricao: "Dose de cachaça premium.",
    custoUnitario: 1.5,
    insumos: [{ nome: "Cachaça", custo: 1.5 }],
    modalityConfig: {
      evento: { active: false, cost: 0 },
      steakhouse: { active: false, cost: 0 },
      goatbotequim: { active: true, cost: 1.5, price: 7.0 },
    },
    imagem:
      "https://images.unsplash.com/photo-1595981267035-7b04ca84a82d?w=400&auto=format&fit=crop",
  },
  {
    id: "dose-vodka",
    nome: "Vodka (Dose)",
    categoria: "Doses",
    descricao: "Dose de vodka standard.",
    custoUnitario: 3.0,
    insumos: [{ nome: "Vodka", custo: 3.0 }],
    modalityConfig: {
      evento: { active: false, cost: 0 },
      steakhouse: { active: false, cost: 0 },
      goatbotequim: { active: true, cost: 3.0, price: 14.0 },
    },
    imagem:
      "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=400&auto=format&fit=crop",
  },
  {
    id: "dose-gin",
    nome: "Gin (Dose)",
    categoria: "Doses",
    descricao: "Dose de gin nacional.",
    custoUnitario: 6.0,
    insumos: [{ nome: "Gin", custo: 6.0 }],
    modalityConfig: {
      evento: { active: false, cost: 0 },
      steakhouse: { active: false, cost: 0 },
      goatbotequim: { active: true, cost: 6.0, price: 17.0 },
    },
    imagem: "/drinks/gin-tonica.png",
  },
  {
    id: "dose-whisky",
    nome: "Whisky (Dose)",
    categoria: "Doses",
    descricao: "Dose de whisky 8 anos.",
    custoUnitario: 10.0,
    insumos: [{ nome: "Whisky", custo: 10.0 }],
    modalityConfig: {
      evento: { active: false, cost: 0 },
      steakhouse: { active: false, cost: 0 },
      goatbotequim: { active: true, cost: 10.0, price: 25.0 },
    },
    imagem: "/drinks/old-fashioned.png",
  },
  {
    id: "dose-campari",
    nome: "Campari (Dose)",
    categoria: "Doses",
    descricao: "Dose de campari.",
    custoUnitario: 7.0,
    insumos: [{ nome: "Campari", custo: 7.0 }],
    modalityConfig: {
      evento: { active: false, cost: 0 },
      steakhouse: { active: false, cost: 0 },
      goatbotequim: { active: true, cost: 7.0, price: 25.0 },
    },
    imagem: "/drinks/negroni.png",
  },
];

export const fichaTecnica = {};

export const margem = (precoVenda: number, custo: number) =>
  ((precoVenda - custo) / precoVenda) * 100;

// ─── Vendas (90 dias) ─────────────────────────────────────────────────────
export const vendas: Venda[] = [];

// ─── Sessões Financeiras (Botequim/Steakhouse) ─────────────────────────────
export const financialSessions: FinancialSession[] = [];

// ─── Eventos ──────────────────────────────────────────────────────────────
export const eventos: Evento[] = [];

// ─── Contratos Legado (to be deprecated) ────────────────────────────────────────────────────────────
export const contratos: Contrato[] = [];

// ─── Inventário ────────────────────────────────────────────────────────────
export const inventoryItems: InventoryItem[] = [
  {
    id: "inv-1",
    nome: "Vodka",
    quantidadeTotal: 20,
    observacoes:
      "- Estoque casa: 8 unidades\n- 7 Steakhouse: 5 unidades\n- Goat Botequim: 4 unidades\n- Evento reservado: 3 unidades",
  },
  {
    id: "inv-2",
    nome: "Gin",
    quantidadeTotal: 12,
    observacoes: "- Estoque casa: 12 unidades",
  },
];

// ─── Novos Módulos de Contrato ────────────────────────────────────────────────
export const contractTemplates: ContractTemplate[] = [
  {
    id: "tpl1",
    name: "Contrato Padrão Eventos",
    fileUrl: "",
    fileType: "docx",
    isDefault: true,
    variablesSchema: ["nome_cliente", "valor_total_evento"],
    createdAt: new Date().toISOString(),
  },
];

export const contractSigners: ContractSigner[] = [
  {
    id: "sig1",
    name: "Sócio Administrador",
    cpf: "000.000.000-00",
    email: "admin@goatbar.com.br",
    phone: "11999999999",
    role: "Sócio Diretor",
    address: "Rua Exemplo, 123",
    isActive: true,
  },
];

export const glasswares: Glassware[] = [
  { id: "g1", name: "Taça Gin", type: "Taça", replacementValue: 45, isActive: true },
  { id: "g2", name: "Copo Long Drink", type: "Copo", replacementValue: 25, isActive: true },
  { id: "g3", name: "Copo Baixo", type: "Copo", replacementValue: 30, isActive: true },
  { id: "g4", name: "Taça Coupe", type: "Taça", replacementValue: 40, isActive: true },
  { id: "g5", name: "Copo Shot", type: "Copo", replacementValue: 15, isActive: true },
];

export const eventContracts: EventContract[] = [];
export const eventContractClientDatas: EventContractClientData[] = [];
export const contractHistories: ContractHistory[] = [];
export const contractSignatureHistories: ContractSignatureHistory[] = [];

// ─── Parâmetros de cálculo ────────────────────────────────────────────────
export const parametros: ParametroCalculo[] = [
  {
    id: "p1",
    chave: "repasse_percentual",
    label: "Percentual de repasse",
    valor: 15,
    unidade: "%",
    groupo: "Repasse",
    descricao: "Aplicado sobre receita líquida",
  },
  {
    id: "p2",
    chave: "custo_equipe_hora",
    label: "Custo de equipe por hora",
    valor: 65,
    unidade: "R$",
    groupo: "Custos",
  },
  {
    id: "p3",
    chave: "custo_operacional_evento",
    label: "Custo operacional fixo por evento",
    valor: 850,
    unidade: "R$",
    groupo: "Custos",
  },
  {
    id: "p4",
    chave: "margem_minima",
    label: "Margem mínima aceitável",
    valor: 35,
    unidade: "%",
    groupo: "Operacional",
  },
  {
    id: "p5",
    chave: "perda_estimada",
    label: "Perdas estimadas",
    valor: 4,
    unidade: "%",
    groupo: "Operacional",
  },
  {
    id: "p6",
    chave: "quebra_copos",
    label: "Quebra de copos por 100 convidados",
    valor: 6,
    unidade: "un",
    groupo: "Operacional",
  },
  {
    id: "p7",
    chave: "consumo_medio_pessoa",
    label: "Consumo médio por convidado",
    valor: 4.5,
    unidade: "doses",
    groupo: "Consumo",
  },
  {
    id: "p8",
    chave: "gelo_kg_pessoa",
    label: "Gelo por convidado",
    valor: 1.2,
    unidade: "kg",
    groupo: "Consumo",
  },
  {
    id: "p9",
    chave: "insumos_pessoa",
    label: "Insumos por convidado",
    valor: 4.5,
    unidade: "R$",
    groupo: "Consumo",
  },
  {
    id: "p10",
    chave: "markup_eventos",
    label: "Markup Eventos",
    valor: 3.5,
    unidade: "x",
    // @ts-expect-error Erro legado pré-existente fora do escopo (Tipagem de BD desatualizada)
    groupo: "Precificação",
  },
  {
    id: "p11",
    chave: "markup_7steakhouse",
    label: "Markup 7Steakhouse",
    valor: 3.0,
    unidade: "x",
    // @ts-expect-error Erro legado pré-existente fora do escopo (Tipagem de BD desatualizada)
    groupo: "Precificação",
  },
  {
    id: "p12",
    chave: "markup_goatbotequim",
    label: "Markup Goat Botequim",
    valor: 2.8,
    unidade: "x",
    // @ts-expect-error Erro legado pré-existente fora do escopo (Tipagem de BD desatualizada)
    groupo: "Precificação",
  },
];

export const tiposEvento: TipoEvento[] = [
  {
    id: "t1",
    nome: "Casamento",
    consumoBebidaPessoa: 5,
    geloKgPessoa: 1.4,
    insumosPessoa: 5,
    equipePor50: 2,
  },
  {
    id: "t2",
    nome: "Corporativo",
    consumoBebidaPessoa: 3.5,
    geloKgPessoa: 1,
    insumosPessoa: 3.5,
    equipePor50: 1.5,
  },
  {
    id: "t3",
    nome: "Aniversário",
    consumoBebidaPessoa: 4.5,
    geloKgPessoa: 1.2,
    insumosPessoa: 4.5,
    equipePor50: 2,
  },
  {
    id: "t4",
    nome: "Comemoração",
    consumoBebidaPessoa: 4,
    geloKgPessoa: 1.1,
    insumosPessoa: 4,
    equipePor50: 1.5,
  },
];

// ─── Cálculos derivados ───────────────────────────────────────────────────
export function calcularEvento(evento: Evento, drinksList: Drink[] = drinks) {
  const tipo = tiposEvento.find((t) => t.nome === evento.tipo) || tiposEvento[0];
  const param = (k: string) => parametros.find((p) => p.chave === k)?.valor ?? 0;

  const dosesEstimadas = evento.convidados * tipo.consumoBebidaPessoa;
  const geloKg = evento.convidados * tipo.geloKgPessoa;
  const insumos = evento.convidados * tipo.insumosPessoa;
  const equipeNec = Math.ceil((evento.convidados / 50) * tipo.equipePor50);

  const custoDrinks = evento.drinks.reduce((acc, dId) => {
    const d = drinksList.find((x) => x.id === dId);
    if (!d) return acc;
    return acc + d.custoUnitario * (dosesEstimadas / Math.max(evento.drinks.length, 1));
  }, 0);

  const custoEquipe = equipeNec * 6 * param("custo_equipe_hora"); // 6h evento
  const custoFixo = param("custo_operacional_evento");
  const custoTotal = custoDrinks + custoEquipe + custoFixo + insumos;
  const repasse = (evento.valorNegociado * param("repasse_percentual")) / 100;
  const lucro = evento.valorNegociado - custoTotal - repasse;
  const margemPct = (lucro / evento.valorNegociado) * 100;

  return {
    dosesEstimadas: Math.round(dosesEstimadas),
    geloKg: Math.round(geloKg),
    insumos,
    equipeNec,
    custoDrinks,
    custoEquipe,
    custoFixo,
    custoTotal,
    repasse,
    lucro,
    margemPct,
    rentabilidadePorConvidado: lucro / evento.convidados,
  };
}

export function calcularOrcamentoEvento(evento: Evento, drinksList: Drink[] = drinks) {
  if (!evento) return null;

  // --- DRINKS ---
  const validDrinks = evento.drinks || [];

  const custoBaseDrinks = validDrinks.reduce((acc, drinkId) => {
    const snap = evento.drinksSnapshots?.find((s) => s.drinkId === drinkId);
    if (snap && typeof snap.custoUnitario === "number") {
      return acc + snap.custoUnitario;
    }
    const drink = drinksList.find((d) => d.id === drinkId || d.nome === drinkId);
    return acc + (drink?.custoUnitario || 0);
  }, 0);

  const convidados = Number(evento.convidados) || 0;
  const drinksPorPessoa = Number(evento.drinksPorPessoa) || 0;
  const totalDoses = convidados * drinksPorPessoa;
  const qtdDrinksSelecionados = validDrinks.length;
  const mediaCustoDrinks = qtdDrinksSelecionados > 0 ? custoBaseDrinks / qtdDrinksSelecionados : 0;

  const markupAdicional = Number(evento.markupAdicionalDrinks) || 0;
  const valorDrinksEvento = totalDoses * mediaCustoDrinks * (1 + markupAdicional / 100);

  // --- EQUIPE ---
  const valorEquipe = Object.values(evento.equipe || {}).reduce(
    (acc, p) => acc + (Number(p.qtd) || 0) * (Number(p.valorUnitario) || 0),
    0,
  );

  // --- GELO ---
  const pacotesGelo = Number(evento.gelo?.pacotesOverride) || Math.ceil((convidados / 100) * 35);
  const valorGelo = pacotesGelo * (Number(evento.gelo?.valorUnitario) || 6);

  // --- LOGÍSTICA ---
  const valorGasolina = evento.viagem?.incluir ? Number(evento.viagem.valor) || 0 : 0;

  // --- GASTOS DIVERSOS ---
  const valorGastosDiversos = (evento.gastosDiversos || []).reduce(
    (acc, g) => acc + (Number(g.valor) || 0),
    0,
  );

  // --- TOTAIS ---
  const { calcularWelcomeDrinks, calcularTotalShots } = requireAdditionalBudgetCalculators();
  const welcomeDrinks = evento.hasWelcomeDrinks
    ? calcularWelcomeDrinks(
        convidados,
        evento.welcomeDrinksPerPerson,
        evento.welcomeDrinksSelected,
        evento.welcomeDrinksProfitPercentage,
      )
    : { custoTotal: 0, valorFinal: 0, totalDrinks: 0, distribuicao: [] };
  const shotsTotal = evento.hasShots ? calcularTotalShots(evento.shotsItems) : 0;
  const custoTotalOrcamentoOriginal =
    valorDrinksEvento +
    valorEquipe +
    valorGelo +
    valorGasolina +
    valorGastosDiversos +
    welcomeDrinks.custoTotal +
    shotsTotal;
  const lucroDesejado = Number(evento.lucroDesejado) || 0;
  const valorTotalSemDesconto =
    custoTotalOrcamentoOriginal +
    lucroDesejado +
    (welcomeDrinks.valorFinal - welcomeDrinks.custoTotal);

  const descontosLista = evento.descontos || [];
  const drinkDiscount = descontosLista
    .filter((d) => !!d.deduzirCustoDrinks)
    .reduce((acc, d) => acc + (Number(d.valor) || 0), 0);
  const regularDiscount = descontosLista
    .filter((d) => !d.deduzirCustoDrinks)
    .reduce((acc, d) => acc + (Number(d.valor) || 0), 0);

  const valorDescontoLista = drinkDiscount + regularDiscount;
  const valorDesconto = valorDescontoLista > 0 ? valorDescontoLista : Number(evento.desconto) || 0;
  const valorTotalOrcamento = valorTotalSemDesconto - valorDesconto;

  const custoTotalOrcamento = Math.max(0, custoTotalOrcamentoOriginal - drinkDiscount);
  const lucro = valorTotalOrcamento - custoTotalOrcamento;
  const mediaPorPessoa = convidados > 0 ? valorTotalOrcamento / convidados : 0;

  const percentualPago = Number(evento.pagamento?.percentualPago) || 0;
  const valorPago = (valorTotalOrcamento * percentualPago) / 100;
  const valorPendente = valorTotalOrcamento - valorPago;
  const percPendente = 100 - percentualPago;

  let statusPagamento = "Não pago";
  if (percentualPago >= 100) statusPagamento = "Pago integralmente";
  else if (percentualPago > 0) statusPagamento = "Parcialmente pago";

  return {
    mediaCustoDrinks,
    custoBaseDrinks,
    valorDrinksEvento,
    valorEquipe,
    pacotesGelo,
    valorGelo,
    valorGasolina,
    valorGastosDiversos,
    lucro,
    valorTotalOrcamento,
    valorTotalSemDesconto,
    valorDesconto,
    custoTotalOrcamento,
    mediaPorPessoa,
    valorPago,
    valorPendente,
    percPendente,
    statusPagamento,
    welcomeDrinks,
    shotsTotal,
  };
}

import {
  calcularTotalShots as calculateShots,
  calcularWelcomeDrinks as calculateWelcome,
} from "./additional-budget-items";
const requireAdditionalBudgetCalculators = () => ({
  calcularTotalShots: calculateShots,
  calcularWelcomeDrinks: calculateWelcome,
});

export function vendasResumo(filtroUnidade?: Unidade) {
  const lista = filtroUnidade ? vendas.filter((v) => v.unidade === filtroUnidade) : vendas;
  const receita = lista.reduce((a, v) => a + v.precoUnitario * v.quantidade, 0);
  const custo = lista.reduce((a, v) => a + v.custoUnitario * v.quantidade, 0);
  const lucro = receita - custo;
  return {
    total: lista.length,
    receita,
    custo,
    lucro,
    margem: (lucro / receita) * 100,
    ticketMedio: receita / lista.length,
  };
}

export function rankingDrinks() {
  const map = new Map<string, { nome: string; qtd: number; receita: number; lucro: number }>();
  vendas.forEach((v) => {
    const cur = map.get(v.drinkId) || { nome: v.drinkNome, qtd: 0, receita: 0, lucro: 0 };
    cur.qtd += v.quantidade;
    cur.receita += v.precoUnitario * v.quantidade;
    cur.lucro += (v.precoUnitario - v.custoUnitario) * v.quantidade;
    map.set(v.drinkId, cur);
  });
  return Array.from(map.values()).sort((a, b) => b.qtd - a.qtd);
}
